// Copyright 2017 Sorama B.V.

import { autoinject, BindingEngine, computedFrom } from "aurelia-framework";
import { FileToUpload } from "controls/file-dropbox/FileDropbox";
import { RecordingContainer } from "datatypes/Containers";
import { DataPart, GenericMeasurement, StorageInfo } from "datatypes/SoramaWebApi";
import { Splice } from "datatypes/Splice";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { FileNameExtensions } from "extensions/FileNameExtensions";
import { MeasurementPartTypeExtensions } from "extensions/MeasurementPartTypeExtensions";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";
import { SoramaPortalDb } from "libraries/IndexedDb/SoramaPortalDb";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { StorageRepository } from "repositories/StorageRepository";
import {
    IMeasurementStorageInfo,
    IMeasurementStorageService,
    IMeasurementUploadStatus,
    SyncStatus,
} from "services/Measurement/IMeasurementStorageService";
import {
    INotificationService,
    IProgressNotificationController,
    NotificationUrgency,
} from "services/Notification/INotificationService";
import { IStorageService } from "services/Storage/IStorageService";
import { ILicenseService } from "../License/ILicenseService";

class MeasurementStorageSyncSetting implements IIndexableObject {
    public userId: string;
    public name: string;
    public id: string;
    public measurementId: string;
    public sorId: string;
    public syncStatus: SyncStatus;
    public errorMessage: string;
}

class MeasurementUploadStatus implements IMeasurementUploadStatus {
    public measurement: GenericMeasurement;

    public storageAllocated: boolean;
    public dataOnline: boolean;
    public dataOffline: boolean;
    public uploading: boolean;

    public totalSize: number;
    public uploadSize: number;

    public errorMessage: string;
}

class MeasurementStorageInfo implements IMeasurementStorageInfo {
    public dataOnline: boolean;
    public storageAllocated: boolean;
    public uploading: boolean;
    public progress: number;
}

@autoinject
export class MeasurementStorageService implements IMeasurementStorageService, IAuthenticationHandler {
    private static readonly ParallelUploads: number = 5;
    private static readonly NotificationSourceName = "Upload Service";

    private measurementRepository: MeasurementRepository;
    private storageRepository: StorageRepository;
    private storageService: IStorageService;
    private soramaPortalDb: SoramaPortalDb;
    private bindingEngine: BindingEngine;
    private notificationService: INotificationService;
    private licenseService: ILicenseService;
    private authentication: IAuthentication;
    private localApi: ILocalApi;
    private webRequestHandler: IWebRequestHandler;

    private storageInfoPromise: Promise<void>;
    private uploadPromise: Promise<void>;

    public outOfCloudStorage = false;
    private uploadInProgress = false;
    private _measurementStorageInfos: { [measurementId: string]: MeasurementStorageInfo } = {};
    private measurementStatus: { [measurementId: string]: MeasurementUploadStatus } = {};

    @computedFrom("_measurementStorageInfos")
    public get measurementStorageInfos(): { [measurementId: string]: IMeasurementStorageInfo } {
        return this._measurementStorageInfos;
    }

    @computedFrom("uploadInProgress")
    public get isUploading(): boolean {
        return this.uploadInProgress;
    }

    constructor(
        measurementRepository: MeasurementRepository,
        authentication: IAuthentication,
        licenseService: ILicenseService,
        storageService: IStorageService,
        storageRepository: StorageRepository,
        notificationService: INotificationService,
        soramaPortalDb: SoramaPortalDb,
        localApi: ILocalApi,
        webRequestHandler: IWebRequestHandler,
        bindingEngine: BindingEngine
    ) {
        this.authentication = authentication;
        this.measurementRepository = measurementRepository;
        this.storageService = storageService;
        this.soramaPortalDb = soramaPortalDb;
        this.notificationService = notificationService;
        this.storageRepository = storageRepository;
        this.licenseService = licenseService;
        this.bindingEngine = bindingEngine;
        this.localApi = localApi;
        this.webRequestHandler = webRequestHandler;

        this.bindingEngine
            .collectionObserver(this.measurementRepository.measurementIds)
            .subscribe(this.measurementIdsChanged);

        this.authentication.addAuthenticationHandler(this);

        if (this.authentication.isSignedIn) {
            this.onSignIn(this.authentication.userId);
        }
    }

    public areMeasurementsAvailable(
        measurementIds: string[]
    ): Promise<{ measurementId: string; available: boolean }[]> {
        const storageInfoPromise = this.storageInfoPromise ? this.storageInfoPromise : Promise.resolve();

        // Wait for any pending storage info promise to complete before checking to prevent duplicate checks.
        const promise = storageInfoPromise.then(() => {
            const availableMeasurementStatus = <{ measurementId: string; available: boolean }[]>[];

            // Check if we have all of the statusses available here, if not request update
            for (const id of measurementIds) {
                const status = this._measurementStorageInfos[id];
                if (status) {
                    availableMeasurementStatus.push({ measurementId: id, available: status.dataOnline });
                }
            }

            if (availableMeasurementStatus.length === measurementIds.length)
                return Promise.resolve(availableMeasurementStatus);

            // Request all others from the server
            return this.updateStorageInfos(
                measurementIds.filter((id) => availableMeasurementStatus.every((x) => x.measurementId !== id))
            ).then(() => {
                const availability = <{ measurementId: string; available: boolean }[]>[];
                for (const id of measurementIds) {
                    const status = this._measurementStorageInfos[id];
                    availability.push({ measurementId: id, available: status && status.dataOnline });
                }

                return availability;
            });
        });

        return promise;
    }

    public getStorageInformation(measurementId: string): Promise<IMeasurementUploadStatus> {
        const info = this.measurementStatus[measurementId]
            ? this.measurementStatus[measurementId]
            : <MeasurementUploadStatus>{};
        this.measurementStatus[measurementId] = info;

        // First of all, get the measurement from the server
        const measurementPromise = this.measurementRepository.getMeasurement(measurementId).then((x) => {
            info.measurement = x;
            info.totalSize = x.parts.map((part) => part.dataSize).reduce((prev, curr) => prev + curr, 0);
        });

        const partsPromise = this.measurementRepository
            .getMeasurementDataParts(measurementId)
            .then((parts) => {
                // Determine remaining size
                info.storageAllocated = true;

                if (!info.uploadSize)
                    info.uploadSize =
                        Math.round(
                            (parts
                                .reduce((prev, curr) => prev.concat(curr), [])
                                .map((x) => x.size)
                                .reduce((prev, curr) => prev + curr, 0) /
                                1024 /
                                1024) *
                                10
                        ) / 10;
                info.dataOnline = info.uploadSize === 0;
            })
            .catch((e) => {
                // Set flags
                info.storageAllocated = false;
                info.dataOnline = false;
            });

        return Promise.all([measurementPromise, partsPromise]).then(() => {
            if (!info.storageAllocated) {
                info.uploadSize = info.totalSize;
            }

            const uploadInProgressPromise = this.soramaPortalDb
                .getAll(SoramaPortalDb.MeasurementStorageSyncStoreName)
                .then((stati: MeasurementStorageSyncSetting[]) => {
                    const status = stati.find((x) => x.measurementId === measurementId);
                    if (!status) return false;

                    return status.syncStatus == SyncStatus.Upload || status.syncStatus == SyncStatus.UploadAndDownload;
                })
                .then((uploadInProgress) => {
                    info.uploading = uploadInProgress && this.uploadInProgress;
                });

            const measurementDataLocalPromises = info.measurement.parts.map((part, index) => {
                return this.storageService.load(part.dataContainer).then((recordingContainer: RecordingContainer) => {
                    const fileName = MeasurementPartTypeExtensions.toFileName(part.measurementType);

                    if (!recordingContainer || !recordingContainer.metadata.files[fileName])
                        return Promise.reject(undefined); // No data for this part
                    else return Promise.resolve(); // Data available for this part
                });
            });

            const measurementDataAvailablePromise = Promise.all(measurementDataLocalPromises)
                .then((x) => {
                    info.dataOffline = true;
                })
                .catch(() => {
                    info.dataOffline = false;
                });

            // Check if we have (some of) the data offline
            return Promise.all([measurementDataAvailablePromise, uploadInProgressPromise]).then(() => info);
        });
    }

    public allocateStorage(measurementId: string): Promise<void> {
        return this.measurementRepository.allocateStorageForMeasurement(measurementId);
    }

    public async setSyncStatus(
        status: SyncStatus,
        measurement: GenericMeasurement | { id: string; name: string; sorId?: string }
    ): Promise<void> {
        try {
            // Get current settings for syncing, and update them
            const measurementStorageSettings: MeasurementStorageSyncSetting[] = await this.soramaPortalDb.getAll(
                SoramaPortalDb.MeasurementStorageSyncStoreName
            );

            const existing = measurementStorageSettings.find((x) => x.measurementId === measurement.id);

            if (existing) {
                await this.soramaPortalDb.putObject(
                    <MeasurementStorageSyncSetting>{
                        id: existing.id,
                        measurementId: measurement.id,
                        name: measurement.name,
                        syncStatus: status,
                        sorId: measurement.sorId,
                    },
                    SoramaPortalDb.MeasurementStorageSyncStoreName
                );
            } else {
                await this.soramaPortalDb.addObject(
                    <MeasurementStorageSyncSetting>{
                        measurementId: measurement.id,
                        name: measurement.name,
                        syncStatus: status,
                        sorId: measurement.sorId,
                    },
                    SoramaPortalDb.MeasurementStorageSyncStoreName
                );
            }

            // Set status objects to uploading
            const state = this.measurementStatus[measurement.id];
            if (state) {
                state.uploading = true;
            }

            const info = this._measurementStorageInfos[measurement.id];
            if (info) {
                info.uploading = true;
            }

            // Upload
            await this.upload();
        } catch (e) {
            console.log(e.message);
        }
    }

    public getAvailableCloudStorage(): Promise<number> {
        return this.storageRepository.getStorageSize().then(async (x) => {
            // Ensure this ActiveMembershipLicense is loaded (if it is already loading, this will wait for it to finish loading)
            await this.licenseService.getActiveMembershipLicense();
            if (
                !this.licenseService.activeMembershipLicense ||
                !this.licenseService.activeMembershipLicense.LicenseKind ||
                !this.licenseService.activeMembershipLicense.LicenseKind.Properties
            ) {
                throw new Error("No licence information available.");
            }

            const totalStorageSize = this.licenseService.activeMembershipLicense.LicenseKind.Properties.StorageBytes;

            if (x + 1024 * 1024 * 10 > totalStorageSize) {
                this.notificationService.registerNotification(
                    "Out of Cloud storage",
                    "Please remove data from your account or increase your data plan.",
                    NotificationUrgency.Warning
                );
                this.outOfCloudStorage = true;
            } else if (x + 1024 * 1024 * 100 > totalStorageSize) {
                this.notificationService.registerNotification(
                    "Almost out of Cloud storage",
                    "Please remove data from your account or increase your data plan.",
                    NotificationUrgency.Info
                );
                this.outOfCloudStorage = true;
            } else {
                this.outOfCloudStorage = false;
            }

            return totalStorageSize - x;
        });
    }

    /// <summary>
    /// Saves the given file locally and returns metadata of the measurement
    /// </summary>
    public async saveSorFileLocally(file: FileToUpload): Promise<GenericMeasurement> {
        const uri = await this.localApi.storage;

        // Send file to acquisition service for parsing and saving.
        const measurement = (
            await this.webRequestHandler.createPostRequest<GenericMeasurement>(`${uri}/Sor`, file.data).send()
        ).content;

        return measurement;
    }

    public async saveSorFileLocallyAndRegister(
        file: FileToUpload,
        projectId: string,
        ownerId: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const measurement = await this.saveSorFileLocally(file).catch((error: HttpResponseMessageError) => {
            console.error(`Error saving .sor file "${file.name}" locally:`, error);
            if (error.exceptionType === "Sorama.SIS.AcquisitionService.Controllers.CorruptedException") {
                throw new Error(
                    "The file appears to be corrupted and cannot be imported. Please contact Sorama support."
                );
            }

            if (error.exceptionType === "System.IO.InvalidDataException") {
                throw new Error(
                    "The file is not a valid sor file and cannot be imported. Please contact Sorama support."
                );
            }

            if (error.exceptionType === "System.NotSupportedException") {
                throw new Error(
                    "The file has an unsupported format or version and cannot be imported. Please contact Sorama support."
                );
            }

            throw new Error("The file cannot not be saved locally. Please contact Sorama support.");
        });

        // saveSorFileLocally tries to send back the data needed to create a genericmeasurement, but it's missing some things
        measurement.projectId = projectId;
        measurement.ownerId = ownerId;
        measurement.name = FileNameExtensions.getName(file.name);

        // Actually create the genericMeasurement in mongoDB
        const genericMeasurement = await this.measurementRepository
            .registerLocallySavedSorFileInCloud(measurement, projectId, progressCallback)
            .catch((error) => {
                console.error(`Error registering locally saved .sor file "${file.name}" in the cloud:`, error);
                throw new Error("The file could not be registered in the cloud. Please contact Sorama support.");
            });
        return genericMeasurement;
    }

    public async uploadSorxToCloud(
        file: FileToUpload,
        projectId: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const name = FileNameExtensions.getName(file.name);

        const genericMeasurement = await this.measurementRepository
            .uploadSorxMeasurementToCloud(file.data, projectId, name, progressCallback)
            .catch((error: HttpResponseMessageError) => {
                console.error(`Error uploading .sorx file "${file.name}" to cloud:`, error);

                if (error.statusCode === 413) {
                    throw new Error("The file is too large to upload. Maximum file size exceeded.");
                }

                if (error.exceptionType === "System.IO.InvalidDataException") {
                    throw new Error(
                        "The file is not a valid sorx file and cannot be imported. Please contact Sorama support."
                    );
                }

                if (error.exceptionType === "System.NotSupportedException") {
                    throw new Error(
                        "The file has an unsupported format or version and cannot be imported. Please contact Sorama support."
                    );
                }

                if (
                    error.message ===
                    "An unexpected error occurred while processing the .sorx file.Audio was not found in the SorX measurement file."
                ) {
                    throw new Error("The file does not contain audio data and cannot be imported.");
                }

                throw new Error("The file could not be uploaded to the cloud. Please contact Sorama support.");
            });

        return genericMeasurement;
    }

    public async registerLocalSor(
        sorId: string,
        projectId: string,
        name?: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const measurementMetadata = await this.storageService.getSorMetadata(sorId);
        measurementMetadata.projectId = projectId;
        measurementMetadata.name = name || measurementMetadata.name;

        const genericMeasurement = await this.measurementRepository.registerLocallySavedSorFileInCloud(
            measurementMetadata,
            projectId,
            progressCallback
        );
        return genericMeasurement;
    }

    private measurementIdsChanged = (splices: Splice<string>[]): void => {
        const newIds: string[] = [];
        for (const splice of splices) {
            newIds.push(
                ...this.measurementRepository.measurementIds.slice(splice.index, splice.index + splice.addedCount)
            );
        }

        this.updateStorageInfos(newIds);
    };

    private updateStorageInfos(ids: string[]): Promise<void> {
        const promise = this.storageInfoPromise ? this.storageInfoPromise : Promise.resolve();

        return (this.storageInfoPromise = promise
            .then(() => {
                // Query for all measurement states
                return this.measurementRepository.getMeasurementStorageInfos(ids).then((x) => {
                    // Set all status infos
                    for (const storageInfo of x) {
                        const existing =
                            this._measurementStorageInfos && this._measurementStorageInfos[storageInfo.measurementId]
                                ? this._measurementStorageInfos[storageInfo.measurementId]
                                : undefined;

                        // Update value
                        this._measurementStorageInfos[storageInfo.measurementId] = {
                            dataOnline: storageInfo.storageInfo === StorageInfo.Available,
                            storageAllocated: storageInfo.storageInfo !== StorageInfo.NotAvailable,
                            uploading: existing ? existing.uploading : false,
                            progress: existing ? existing.progress : 0,
                        };
                    }
                });
            })
            .catch(() => {}));
    }

    private async upload(): Promise<void> {
        // Only one upload 'thread' should be active
        if (!this.uploadPromise) {
            this.uploadPromise = this.uploadAsync();
        }

        await this.uploadPromise;

        this.uploadPromise = null;
    }

    private async uploadAsync(): Promise<void> {
        const measurementStorageSetting = await this.getFirstAvailableUploadMeasurement();

        if (!measurementStorageSetting) {
            return; // Done uploading
        }

        try {
            const progressNotification = this.notificationService.registerProgressNotification(
                MeasurementStorageService.NotificationSourceName,
                "Uploading measurement '" + measurementStorageSetting.name + "'"
            );
            this.setMeasurementStatus(measurementStorageSetting.measurementId, StorageInfo.Allocated, true);

            // Check if we already have the measurement, else get it from the server
            const measurement = this.measurementStatus[measurementStorageSetting.measurementId]
                ? this.measurementStatus[measurementStorageSetting.measurementId].measurement
                : await this.measurementRepository.getMeasurement(measurementStorageSetting.measurementId);

            try {
                // Check if the measurement data is available now
                // Throws exception if measurement is not available
                await this.isMeasurementDataAvailableOffline(measurement);

                // Allocate storage
                await this.measurementRepository.allocateStorageForMeasurement(measurement.id);

                // Upload the measurement
                if (measurementStorageSetting.sorId) {
                    const rawSorBlob = await this.storageService.getRawSor(measurementStorageSetting.sorId);
                    await this.measurementRepository.uploadSorMeasurementToCloud(
                        rawSorBlob,
                        measurement.projectId,
                        measurement.name,
                        "sor",
                        (progress) => progressNotification.setProgress(progress.progressEvent.loaded)
                    );
                } else {
                    await this.uploadMeasurement(measurementStorageSetting.id, measurement, progressNotification);
                }

                // Set status
                await this.setMeasurementStatusComplete(measurementStorageSetting.id);

                // Propagate to UI - Replace notification with result
                this.setMeasurementStatus(measurementStorageSetting.measurementId, StorageInfo.Available, false);
                progressNotification.dispose();
                this.notificationService.registerNotification(
                    MeasurementStorageService.NotificationSourceName,
                    "Upload completed '" + measurementStorageSetting.name + "'"
                );
            } catch (e) {
                progressNotification.dispose();

                if (e.statusCode === 413) {
                    // Out of storage
                    this.notificationService.registerNotification(
                        MeasurementStorageService.NotificationSourceName,
                        "Insufficient Cloud Storage to store: '" + measurementStorageSetting.name + "':",
                        NotificationUrgency.Error
                    );
                } else {
                    // Other error
                    this.notificationService.registerNotification(
                        MeasurementStorageService.NotificationSourceName,
                        "Error uploading '" + measurementStorageSetting.name + (e.message ? "':" + e.message : "'"),
                        NotificationUrgency.Error
                    );
                }

                this.setMeasurementStatus(measurementStorageSetting.measurementId, StorageInfo.NotAvailable, false);
                await this.setMeasurementStatusError(measurementStorageSetting.id, e.message);
            }

            // Restart function to check for next queued uploads
            await this.uploadAsync();
        } catch (e) {
            console.error("Unable to upload measurement data, " + e.message);
        }
    }

    private getFirstAvailableUploadMeasurement(): Promise<MeasurementStorageSyncSetting> {
        return this.soramaPortalDb
            .getAll(SoramaPortalDb.MeasurementStorageSyncStoreName)
            .then((x: MeasurementStorageSyncSetting[]) => {
                return x.find(
                    (x) => x.syncStatus === SyncStatus.Upload || x.syncStatus === SyncStatus.UploadAndDownload
                );
            });
    }

    // This only returns true if ALL measurement data is available offline
    private isMeasurementDataAvailableOffline(measurement: GenericMeasurement): Promise<void> {
        if (measurement.sorId && measurement.sorId.length > 0) {
            return this.storageService.getSorMetadata(measurement.sorId).then((measurement) => {
                if (!measurement || !measurement.sorId)
                    throw new Error("Measurement data is not available on this computer");
            });
        }

        // Retrieve all recording containers
        const promises = measurement.parts.map((part, index) => {
            // Compute hash for all containers
            return this.storageService
                .getMD5Hash(part.dataContainer, MeasurementPartTypeExtensions.toFileName(part.measurementType))
                .then((hash) => {
                    if (hash.hash === part.dataHash && hash.size === part.dataSize) return;

                    throw new Error("Measurement data is not available on this computer");
                });
        });

        return Promise.all(promises).then(() => {});
    }

    private uploadMeasurement(
        statusKey: string,
        measurement: GenericMeasurement,
        notification: IProgressNotificationController
    ): Promise<void> {
        //Entire block bypasses the normal parts/chunking to just upload an entire .sor file.
        if (measurement.sorId && measurement.sorId.length > 0) {
            return this.storageService.getRawSor(measurement.sorId).then((rawBlob) => {
                return this.measurementRepository.uploadMeasurement(measurement.sorId, rawBlob).then(() => {
                    const status = this.measurementStatus[measurement.id];
                    if (status) {
                        //Uploading whole file only, so status goes to 1 instantly.
                        const progress = 1;
                        notification.setProgress(progress);

                        //Update upload status
                        const info = this._measurementStorageInfos[measurement.id];
                        if (info) {
                            info.progress = progress;
                        }
                    }
                });
            });
        }

        return this.measurementRepository.getMeasurementDataParts(measurement.id).then((chunks) => {
            const status = this.measurementStatus[measurement.id];
            if (status)
                status.uploadSize = chunks
                    .map((x) => x.map((y) => y.size).reduce((a, b) => a + b, 0))
                    .reduce((a, b) => a + b, 0);

            // Foreach construction for promises:
            let uploadPartAction: () => Promise<void>;
            let partIndex = 0;

            uploadPartAction = () => {
                return this.uploadMeasurementPart(
                    statusKey,
                    measurement,
                    partIndex,
                    chunks[partIndex],
                    notification
                ).then(() => {
                    partIndex++;
                    if (partIndex < measurement.parts.length) return uploadPartAction();
                });
            };

            return uploadPartAction();
        });
    }

    private uploadMeasurementPart(
        statusKey: string,
        measurement: GenericMeasurement,
        partIndex: number,
        chunks: DataPart[],
        notification: IProgressNotificationController
    ): Promise<void> {
        const remainingChunks = chunks;
        const chunkIdsInProgress: number[] = [];
        let uploadAction: () => Promise<void>;

        uploadAction = () => {
            // Check if the state is still set to upload
            return this.isUploadStatusSet(statusKey).then((upload) => {
                if (!upload) return; // Done

                // Determine which chunk to upload next
                const newChunks = remainingChunks.filter((chunk) => chunkIdsInProgress.every((id) => chunk.id !== id));
                if (newChunks.length === 0) return; // Done

                // Step somewhat randomly through the set of chunks so we might avoid collisions if multiple browsers are open
                const index = Math.floor(Math.random() * newChunks.length);
                const newChunk = newChunks[index];

                // Flag this chunk id as in progress
                chunkIdsInProgress.push(newChunk.id);

                return this.uploadChunk(measurement, partIndex, newChunk, notification).then((remChunks) => {
                    // Remove chunk from the list
                    let index = remainingChunks.findIndex((x) => x === newChunk);
                    remainingChunks.splice(index, 1);

                    index = chunkIdsInProgress.findIndex((x) => x === newChunk.id);
                    if (index !== -1) chunkIdsInProgress.splice(index, 1);

                    // Restart!
                    return uploadAction();
                });
            });
        };

        const promises: Promise<void>[] = [];
        for (let i = 0; i < MeasurementStorageService.ParallelUploads; i++) {
            promises.push(uploadAction());
        }

        return Promise.all(promises).then(() => {});
    }

    private uploadChunk(
        measurement: GenericMeasurement,
        partIndex: number,
        chunk: DataPart,
        notification: IProgressNotificationController
    ): Promise<DataPart[]> {
        const part = measurement.parts[partIndex];

        // Get data for this one and upload it
        return this.storageService
            .getRaw(
                part.dataContainer,
                MeasurementPartTypeExtensions.toFileName(part.measurementType),
                chunk.offset,
                chunk.size
            )
            .then((rawBlob) => {
                return this.measurementRepository
                    .uploadMeasurementPartChunk(measurement.id, partIndex, chunk.id, rawBlob)
                    .then((parts) => {
                        const status = this.measurementStatus[measurement.id];
                        if (status) {
                            status.uploadSize -= chunk.size;

                            const progress = Math.round((1.0 - status.uploadSize / status.totalSize) * 100) / 100;
                            notification.setProgress(progress);

                            const info = this._measurementStorageInfos[measurement.id];
                            if (info) {
                                info.progress = progress;
                            }
                        }

                        return parts;
                    });
            });
    }

    private isUploadStatusSet(key: string): Promise<boolean> {
        return this.soramaPortalDb
            .getObject<MeasurementStorageSyncSetting>(key, SoramaPortalDb.MeasurementStorageSyncStoreName)
            .then(
                (setting) =>
                    setting.syncStatus === SyncStatus.Upload || setting.syncStatus === SyncStatus.UploadAndDownload
            );
    }

    private setMeasurementStatusError(key: string, errorMessage: string): Promise<void> {
        return this.soramaPortalDb
            .getObject<MeasurementStorageSyncSetting>(key, SoramaPortalDb.MeasurementStorageSyncStoreName)
            .then((setting) => {
                setting.errorMessage = errorMessage;
                setting.syncStatus = SyncStatus.Error;

                return this.soramaPortalDb.putObject<MeasurementStorageSyncSetting>(
                    setting,
                    SoramaPortalDb.MeasurementStorageSyncStoreName
                );
            })
            .then(() => {})
            .catch(() => {});
    }

    private setMeasurementStatusComplete(key: string): Promise<void> {
        return this.soramaPortalDb
            .getObject<MeasurementStorageSyncSetting>(key, SoramaPortalDb.MeasurementStorageSyncStoreName)
            .then((setting) => {
                setting.errorMessage = undefined;
                setting.syncStatus = SyncStatus.None;

                return this.soramaPortalDb.putObject<MeasurementStorageSyncSetting>(
                    setting,
                    SoramaPortalDb.MeasurementStorageSyncStoreName
                );
            })
            .then(() => {})
            .catch(() => {});
    }

    private setMeasurementStatus(measurementId: string, state: StorageInfo, isUploading: boolean) {
        const info = this._measurementStorageInfos[measurementId];
        if (info) {
            info.dataOnline = state === StorageInfo.Available;
            info.uploading = isUploading;
            info.storageAllocated = state !== StorageInfo.NotAvailable;
        }

        const status = this.measurementStatus[measurementId];
        if (status) {
            status.uploading = isUploading;
            status.dataOnline = state == StorageInfo.Available;
            status.storageAllocated = state != StorageInfo.NotAvailable;

            if (state == StorageInfo.Available) status.uploadSize = 0;
        }
    }

    public onSignIn(userId: string): Promise<void> {
        this.upload();
        this.getAvailableCloudStorage().catch();

        return Promise.resolve();
    }

    public onSignOut(): Promise<void> {
        return Promise.resolve();
    }
}
